import * as Dialog from '@radix-ui/react-dialog'
import { X, Shield, ShieldCheck, LogOut, Check } from 'lucide-react'
import { useUser } from '../hooks/useUser'

interface ProfileModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

type Level = 'read' | 'readwrite'

interface PermissionGroup {
  category: string
  level: Level
  description: string
}

const LEVEL_LABELS: Record<Level, string> = {
  read: 'Read',
  readwrite: 'Read & write',
}

function formatCategory(category: string): string {
  return category
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

// Collapse the flat permission list into one row per resource category, mirroring
// the No access / Read / Read & write model used on the admin Roles screen.
function groupPermissions(
  details: { key: string; description?: string | null; category: string }[],
): PermissionGroup[] {
  const byCategory = new Map<string, { level: Level; description: string }>()
  for (const p of details) {
    const isWrite = !p.key.endsWith('.read')
    const existing = byCategory.get(p.category)
    const level: Level = isWrite || existing?.level === 'readwrite' ? 'readwrite' : 'read'
    // Prefer the read key's description as the category summary; fall back to any.
    const description = existing?.description || p.description || ''
    byCategory.set(p.category, { level, description })
  }
  return Array.from(byCategory.entries())
    .map(([category, v]) => ({ category, level: v.level, description: v.description }))
    .sort((a, b) => a.category.localeCompare(b.category))
}

export default function ProfileModal({ open, onOpenChange }: ProfileModalProps) {
  const user = useUser()

  const displayName =
    user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.firstName || user.name || user.email
  const initial = (user.firstName || user.name || user.email).charAt(0).toUpperCase()

  const roleNames = user.roles.map((r) => r.name)
  const roleLabel = user.isAdmin
    ? 'Administrator'
    : roleNames.length > 0
      ? roleNames.join(', ')
      : 'Member'

  const permissionGroups = groupPermissions(user.permissionDetails)
  const hasSpecialAccess = !user.isAdmin && permissionGroups.length > 0

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[calc(100%-2rem)] max-w-md bg-card border border-border rounded-lg shadow-lg p-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95">
          <Dialog.Close asChild>
            <button
              className="absolute right-4 top-4 p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </Dialog.Close>

          <div className="p-6">
            {/* Identity */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                <span className="text-xl font-semibold">{initial}</span>
              </div>
              <div className="min-w-0">
                <Dialog.Title className="text-lg font-semibold truncate">{displayName}</Dialog.Title>
                <p className="text-sm text-muted-foreground truncate">{user.email}</p>
              </div>
            </div>

            {/* Role */}
            <div className="mb-5">
              <p className="text-xs font-medium text-muted-foreground mb-2">ROLE</p>
              <div className="flex items-center gap-2">
                {user.isAdmin ? (
                  <ShieldCheck className="w-4 h-4 text-amber-500 flex-shrink-0" />
                ) : (
                  <Shield className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                )}
                <span className="font-medium">{roleLabel}</span>
                {user.isAdmin && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-amber-500/20 text-amber-500 rounded">
                    Admin
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1.5">
                {user.isAdmin
                  ? 'Full access to every platform setting and admin screen.'
                  : hasSpecialAccess
                    ? 'You have additional access beyond a standard member (see below).'
                    : 'Standard access — create, deploy, and manage your own apps.'}
              </p>
            </div>

            {/* Special permissions (non-admins with grants) */}
            {hasSpecialAccess && (
              <div className="mb-2">
                <p className="text-xs font-medium text-muted-foreground mb-2">SPECIAL PERMISSIONS</p>
                <ul className="space-y-1.5">
                  {permissionGroups.map((g) => (
                    <li
                      key={g.category}
                      className="flex items-start gap-2.5 px-3 py-2 rounded-lg bg-secondary/50"
                    >
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{formatCategory(g.category)}</span>
                          <span className="px-1.5 py-0.5 text-[10px] font-medium bg-primary/10 text-primary rounded">
                            {LEVEL_LABELS[g.level]}
                          </span>
                        </div>
                        {g.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{g.description}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Sign out */}
            <a
              href="/auth/logout"
              className="flex items-center justify-center gap-2 w-full mt-6 px-4 py-2.5 border border-border rounded-lg text-sm font-medium hover:bg-secondary transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </a>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
